import { linkComparisonCode, linkHash, newLinkKeys, openDeviceLink, sealDeviceLink, validateLinkQr } from "@link/crypto/device-link";
import { fromBase64url as bytes, toBase64url as b64 } from "@link/shared/api/passkeys";
import { linkContext, linkRequestSchema, type LinkQr, type LinkRequest, type LinkPacket } from "@link/shared/api/device-links";
import { fetchJson } from "./http";
import { devicePhraseForAccount } from "./device-vault";
import { loadIdentity } from "./identity";
const route = "/v1/device-links";
export async function startDeviceLink() {
  const keys = await newLinkKeys();
  const secret = b64(crypto.getRandomValues(new Uint8Array(32)));
  const pollToken = b64(crypto.getRandomValues(new Uint8Array(32)));
  const request = linkRequestSchema.parse(await fetchJson<LinkRequest>(route + "/start", { method: "POST", token: null, body: { targetPublicKey: keys.publicKey, secretHash: await linkHash(bytes(secret)), pollHash: await linkHash(bytes(pollToken)) } }));
  if (request.targetPublicKey !== keys.publicKey) throw new Error("Device-link key changed");
  const qr = await validateLinkQr({ ...request, secret }, location.origin);
  return { qr, privateKey: keys.privateKey, pollToken, code: await linkComparisonCode(qr), url: `${location.origin}/link-device#${b64(new TextEncoder().encode(JSON.stringify(qr)))}` };
}
export type PendingDeviceLink = Awaited<ReturnType<typeof startDeviceLink>>;
export async function pollDeviceLink(link: PendingDeviceLink) {
  if (Date.now() >= link.qr.expiresAt) throw new Error("This code expired. Get a new code to continue.");
  const response = await fetchJson<{ status: "waiting" | "approved"; packet?: LinkPacket }>(route + "/poll", { method: "POST", token: null, body: { flowId: link.qr.flowId, pollToken: link.pollToken } });
  return response.status === "approved" && response.packet ? openDeviceLink(link.qr, link.privateKey, response.packet) : null;
}
export async function closeDeviceLink(link: PendingDeviceLink) {
  await fetchJson(route + "/close", { method: "POST", token: null, body: { flowId: link.qr.flowId, pollToken: link.pollToken } });
}
export async function parseDeviceLink(url: string) {
  const value = new URL(url);
  if (url.length > 2400 || value.origin !== location.origin || value.pathname !== "/link-device" || value.search) throw new Error("Use a device-link QR code from this Link site only.");
  return validateLinkQr(JSON.parse(new TextDecoder().decode(bytes(value.hash.slice(1)))), location.origin);
}
export async function inspectDeviceLink(qr: LinkQr) {
  await validateLinkQr(qr, location.origin);
  const request = linkRequestSchema.parse(await fetchJson<LinkRequest>(route + "/inspect", { method: "POST", body: { flowId: qr.flowId } }));
  if (JSON.stringify(linkContext(request)) !== JSON.stringify(linkContext(qr))) throw new Error("This link was changed. Cancel and scan a new code.");
  return linkComparisonCode(qr);
}
export async function approveDeviceLink(qr: LinkQr, accountXid: string, username: string) {
  await inspectDeviceLink(qr);
  const phrase = await devicePhraseForAccount(accountXid);
  if (!phrase) throw new Error("This older account needs a one-time device setup. Open Settings → Account → This device, then use your emergency backup or an existing passkey.");
  const identity = await loadIdentity(accountXid);
  if (!identity) throw new Error("Sign in again before linking another device.");
  const packet = await sealDeviceLink(qr, phrase, accountXid, username);
  if (packet.identityPublicKey !== b64(identity.publicKeyRaw)) throw new Error("Saved account identity mismatch");
  await fetchJson(route + "/approve", { method: "POST", body: { flowId: qr.flowId, packet } });
}
